import { NumberInput as ANumberInput } from '@ark-ui/solid';
import { IconChevronDown } from '@bbr/icons/chevron-down-outline';
import { IconChevronUp } from '@bbr/icons/chevron-up-outline';
import { Show, splitProps } from 'solid-js';
import { cn } from 'tailwind-variants';
import { Button } from './button.tsx';
import { Field } from './field.tsx';
import { disableAutocomplete } from './form.tsx';
import type { ComponentProps, JSXElement } from 'solid-js';

export function NumberInput(props: ComponentProps<typeof ANumberInput.Root> & { name: string }) {
	return (
		<Field name={props.name}>
			<ANumberInput.Root {...props} class="flex flex-col gap-1">
				{props.children}
			</ANumberInput.Root>
		</Field>
	);
}

NumberInput.Label = (props: ComponentProps<typeof ANumberInput.Label>) => (
	<ANumberInput.Label {...props} class={cn('text-lg font-medium', props.class)} />
);

NumberInput.Control = (props: ComponentProps<typeof ANumberInput.Input>) => {
	return (
		<ANumberInput.Control class="relative flex w-full items-center justify-between gap-x-1 rounded-md border border-zinc-300 bg-white ps-3 pe-0.5 text-lg transition-all outline-none group-hover/field:ring-4 group-hover/field:ring-yellow-400/30 has-focus-visible:ring-4 has-focus-visible:ring-yellow-500/50 has-focus-visible:ring-offset-1 has-focus-visible:ring-offset-zinc-50 has-disabled:bg-zinc-100 has-disabled:text-zinc-500 has-aria-invalid:border-rose-600 has-aria-readonly:cursor-not-allowed has-aria-readonly:bg-zinc-100 has-aria-readonly:text-zinc-500 data-com-onepassword-filled:bg-clip-text motion-safe:has-disabled:active:animate-shake dark:border-zinc-700 dark:bg-black dark:has-focus-visible:ring-offset-zinc-950 dark:has-disabled:bg-zinc-800">
			<ANumberInput.Input
				class="shrink grow py-2 outline-none data-com-onepassword-filled:bg-clip-text"
				tabindex={0}
				{...props}
				{...disableAutocomplete(props)}
			/>
			<div class="flex flex-col">
				<ANumberInput.IncrementTrigger
					asChild={(props) => (
						// @ts-expect-error too complex
						<Button {...props()} type="button" size="sm" rounding="md" appearance="subtle" />
					)}
				>
					<IconChevronUp />
				</ANumberInput.IncrementTrigger>
				<ANumberInput.DecrementTrigger
					asChild={(props) => (
						// @ts-expect-error too complex
						<Button {...props()} type="button" size="sm" rounding="md" appearance="subtle" />
					)}
				>
					<IconChevronDown />
				</ANumberInput.DecrementTrigger>
			</div>
		</ANumberInput.Control>
	);
};

export function NumberField(
	props: ComponentProps<typeof NumberInput> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	return (
		<NumberInput {...rest}>
			<NumberInput.Label>{other.label}</NumberInput.Label>
			<NumberInput.Control />
			<Field.Errors />
			<Show when={other.description}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</NumberInput>
	);
}
